import React, { useState, useEffect } from 'react';
import { 
  getVenues, 
  createVenue, 
  getFieldsByVenue, 
  createField,
  deleteField,
  updateVenue,
  deleteVenue,
  updateField
} from '../../services/api';
import Loading from '../Common/Loading';
import '../../styles/VenueManagement.css';

interface Venue {
  id: number;
  name: string;
  location: string;
  createdAt: string;
  fields?: Field[];
}

interface Field {
  id: string;
  name: string;
  venueId: number;
  createdAt: string;
}

const VenueManagement: React.FC = () => {
  // State variables
  const [venues, setVenues] = useState<Venue[]>([]);
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);
  const [fields, setFields] = useState<Field[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // Localized loading states
  const [loadingVenues, setLoadingVenues] = useState<boolean>(false);
  const [loadingFields, setLoadingFields] = useState<boolean>(false);
  const [updatingVenueId, setUpdatingVenueId] = useState<number | null>(null);
  const [updatingFieldId, setUpdatingFieldId] = useState<string | null>(null);
  
  // Form state
  const [newVenueName, setNewVenueName] = useState<string>('');
  const [newVenueLocation, setNewVenueLocation] = useState<string>('');
  const [newFieldName, setNewFieldName] = useState<string>('');
  
  // Edit state
  const [editingVenue, setEditingVenue] = useState<boolean>(false);
  const [editVenueName, setEditVenueName] = useState<string>('');
  const [editVenueLocation, setEditVenueLocation] = useState<string>('');
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  const [editFieldName, setEditFieldName] = useState<string>('');
  
  // UI state
  const [isAddingVenue, setIsAddingVenue] = useState<boolean>(false);
  const [isAddingField, setIsAddingField] = useState<boolean>(false);

  // Fetch venues on mount
  useEffect(() => {
    fetchVenues();
  }, []);

  // Fetch fields when a venue is selected
  useEffect(() => {
    if (selectedVenue) {
      fetchFieldsForVenue(selectedVenue.id.toString());
    } else {
      setFields([]);
    }
  }, [selectedVenue]);

  // Function to fetch all venues
  const fetchVenues = async () => {
    try {
      setLoadingVenues(true);
      setError(null);
      const venuesData = await getVenues();
      setVenues(venuesData);
      
      // If venues exist and no venue is selected, select the first one
      if (venuesData.length > 0 && !selectedVenue) {
        setSelectedVenue(venuesData[0]);
      } else if (selectedVenue) {
        // If a venue was previously selected, update its data
        const updatedVenue = venuesData.find((v: Venue) => v.id === selectedVenue.id);
        if (updatedVenue) {
          setSelectedVenue(updatedVenue);
        } else if (venuesData.length > 0) {
          // If the selected venue no longer exists, select the first one
          setSelectedVenue(venuesData[0]);
        } else {
          setSelectedVenue(null);
        }
      }
    } catch (err: any) {
      console.error('Failed to fetch venues:', err);
      setError(err.response?.data?.message || 'Failed to load venues. Please try again.');
    } finally {
      setLoadingVenues(false);
    }
  };

  // Function to fetch fields for a specific venue
  const fetchFieldsForVenue = async (venueId: string) => {
    try {
      setLoadingFields(true);
      const fieldsData = await getFieldsByVenue(venueId);
      setFields(fieldsData);
    } catch (err: any) {
      console.error('Failed to fetch fields:', err);
      setError(err.response?.data?.message || 'Failed to load fields. Please try again.');
    } finally {
      setLoadingFields(false);
    }
  };

  // Function to handle venue selection
  const handleVenueSelect = (venue: Venue) => {
    // Ignore selection if currently editing a venue
    if (editingVenue) {
      return;
    }
    
    setSelectedVenue(venue);
    setError(null);
    setEditingVenue(false);
    setEditingFieldId(null);
  };

  // Function to add a new venue
  const handleAddVenue = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newVenueName || !newVenueLocation) {
      setError('Please fill in all required fields.');
      return;
    }
    
    try {
      setLoadingVenues(true);
      setError(null);
      
      await createVenue({
        name: newVenueName,
        location: newVenueLocation,
      });
      
      // Reset form
      setNewVenueName('');
      setNewVenueLocation('');
      setIsAddingVenue(false);
      
      // Update venues list
      await fetchVenues();
    } catch (err: any) {
      console.error('Failed to create venue:', err);
      setError(err.response?.data?.message || 'Failed to create venue. Please try again.');
    } finally {
      setLoadingVenues(false);
    }
  };

  // Function to add a new field to the selected venue
  const handleAddField = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedVenue) {
      setError('Please select a venue first.');
      return;
    }
    
    if (!newFieldName) {
      setError('Please enter a field name.');
      return;
    }
    
    try {
      setLoadingVenues(true);
      setError(null);
      
      await createField(newFieldName, selectedVenue.id.toString());
      
      // Reset form
      setNewFieldName('');
      setIsAddingField(false);
      
      // Refresh fields
      await fetchFieldsForVenue(selectedVenue.id.toString());
    } catch (err: any) {
      console.error('Failed to create field:', err);
      setError(err.response?.data?.message || 'Failed to create field. Please try again.');
    } finally {
      setLoadingVenues(false);
    }
  };

  // Function to delete a field
  const handleDeleteField = async (fieldId: string) => {
    if (!window.confirm('Are you sure you want to delete this field?')) {
      return;
    }
    
    try {
      setLoadingVenues(true);
      setError(null);
      
      await deleteField(fieldId);
      
      // Refresh fields
      if (selectedVenue) {
        await fetchFieldsForVenue(selectedVenue.id.toString());
      }
    } catch (err: any) {
      console.error('Failed to delete field:', err);
      setError(err.response?.data?.message || 'Failed to delete field. Please try again.');
    } finally {
      setLoadingVenues(false);
    }
  };
  
  // Function to initiate venue editing
  const handleEditVenueClick = () => {
    if (selectedVenue) {
      // Reset any previous edit values first
      setEditVenueName('');
      setEditVenueLocation('');
      
      // Set a small delay to ensure state is cleared before setting new values
      setTimeout(() => {
        if (selectedVenue) {
          setEditVenueName(selectedVenue.name);
          setEditVenueLocation(selectedVenue.location);
          setEditingVenue(true);
        }
      }, 0);
    }
  };
  
  // Function to update venue name
  const handleUpdateVenue = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedVenue) {
      return;
    }
    
    if (!editVenueName) {
      setError('Venue name cannot be empty');
      return;
    }
    
    try {
      setUpdatingVenueId(selectedVenue.id);
      setError(null);
      
      await updateVenue(selectedVenue.id.toString(), {
        name: editVenueName,
        location: editVenueLocation
      });
      
      // Clear edit state immediately
      setEditingVenue(false);
      setEditVenueName('');
      setEditVenueLocation('');
      
      // Fetch fresh venue data
      await fetchVenues();
    } catch (err: any) {
      console.error('Failed to update venue:', err);
      setError(err.response?.data?.message || 'Failed to update venue. Please try again.');
    } finally {
      setUpdatingVenueId(null);
    }
  };
  
  // Function to delete a venue
  const handleDeleteVenue = async () => {
    if (!selectedVenue) {
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete venue "${selectedVenue.name}" and all its fields?`)) {
      return;
    }
    
    try {
      setUpdatingVenueId(selectedVenue.id);
      setError(null);
      
      await deleteVenue(selectedVenue.id.toString());
      
      await fetchVenues();
    } catch (err: any) {
      console.error('Failed to delete venue:', err);
      setError(err.response?.data?.message || 'Failed to delete venue. Please try again.');
    } finally {
      setUpdatingVenueId(null);
    }
  };
  
  // Function to initiate field editing
  const handleEditFieldClick = (field: Field) => {
    // Reset field edit state first
    setEditingFieldId(null);
    setEditFieldName('');
    
    // Set a small delay to ensure state is cleared before setting new values
    setTimeout(() => {
      setEditingFieldId(field.id);
      setEditFieldName(field.name);
    }, 0);
  };
  
  // Function to update field name
  const handleUpdateField = async (e: React.FormEvent | React.MouseEvent, fieldId: string) => {
    e.preventDefault();
    
    if (!editFieldName) {
      setError('Field name cannot be empty');
      return;
    }
    
    try {
      setUpdatingFieldId(fieldId);
      setError(null);
      
      await updateField(fieldId, editFieldName);
      
      // Clear edit state
      setEditingFieldId(null);
      setEditFieldName('');
      
      // Refresh fields data
      if (selectedVenue) {
        await fetchFieldsForVenue(selectedVenue.id.toString());
      }
    } catch (err: any) {
      console.error('Failed to update field:', err);
      setError(err.response?.data?.message || 'Failed to update field. Please try again.');
    } finally {
      setUpdatingFieldId(null);
    }
  };
  
  // Function to cancel field editing
  const handleCancelFieldEdit = () => {
    setEditingFieldId(null);
    setEditFieldName('');
  };

  return (
    <div className="venue-management">
      <h2>Venue Management</h2>
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      
      <div className="venue-management-layout">
        <div className="venue-list-section">
          <div className="section-header">
            <h3>Venues</h3>
            <button
              className="add-button"
              onClick={() => setIsAddingVenue(!isAddingVenue)}
              disabled={loadingVenues}
            >
              {isAddingVenue ? 'Cancel' : '+ Add Venue'}
            </button>
          </div>
          
          {loadingVenues && venues.length === 0 && (
            <div className="section-loading">
              <Loading compact message="Loading venues..." />
            </div>
          )}
          
          {isAddingVenue && (
            <div className="form-container">
              <form onSubmit={handleAddVenue}>
                <div className="form-group">
                  <label>Venue Name:</label>
                  <input
                    type="text"
                    value={newVenueName}
                    onChange={(e) => setNewVenueName(e.target.value)}
                    placeholder="Enter venue name"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Location:</label>
                  <input
                    type="text"
                    value={newVenueLocation}
                    onChange={(e) => setNewVenueLocation(e.target.value)}
                    placeholder="Enter venue location"
                    required
                  />
                </div>
                
                <div className="form-actions">
                  <button type="submit" className="submit-button" disabled={loadingVenues}>
                    {loadingVenues ? 'Creating...' : 'Create Venue'}
                  </button>
                </div>
              </form>
            </div>
          )}
          
          <div className="venues-list">
            {venues.length === 0 && !loadingVenues ? (
              <div className="no-items-message">No venues available.</div>
            ) : (
              <ul className="venue-items">
                {venues.map(venue => (
                  <li 
                    key={venue.id}
                    className={`venue-item ${selectedVenue?.id === venue.id ? 'selected' : ''} ${updatingVenueId === venue.id ? 'updating' : ''}`}
                  >
                    {updatingVenueId === venue.id && (
                      <div className="item-loading-overlay">
                        <Loading compact message="" />
                      </div>
                    )}
                    {editingVenue && selectedVenue?.id === venue.id ? (
                      <>
                        <div className="venue-edit-form">
                          <div className="venue-edit-field">
                            <input
                              type="text"
                              value={editVenueName}
                              onChange={(e) => setEditVenueName(e.target.value)}
                              placeholder="Enter venue name"
                              required
                            />
                          </div>
                          <div className="venue-edit-field">
                            <input
                              type="text"
                              value={editVenueLocation}
                              onChange={(e) => setEditVenueLocation(e.target.value)}
                              placeholder="Enter venue location"
                              required
                            />
                          </div>
                        </div>
                        <div className="venue-item-actions">
                          <button
                            className="save-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUpdateVenue(e as React.FormEvent);
                            }}
                            disabled={updatingVenueId === venue.id}
                          >
                            Save
                          </button>
                          <button
                            className="cancel-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingVenue(false);
                              setEditVenueName('');
                              setEditVenueLocation('');
                            }}
                            disabled={updatingVenueId === venue.id}
                          >
                            Cancel
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="venue-info" onClick={() => handleVenueSelect(venue)}>
                          <span className="venue-name">{venue.name}</span>
                          <span className="venue-location">{venue.location}</span>
                        </div>
                        <div className="venue-item-actions">
                          <button
                            className="edit-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              // First select the venue
                              setSelectedVenue(venue);
                              // Reset the edit state values
                              setEditVenueName('');
                              setEditVenueLocation('');
                              // Set a small delay to ensure states are updated correctly
                              setTimeout(() => {
                                // Then set the edit values from the selected venue
                                setEditVenueName(venue.name);
                                setEditVenueLocation(venue.location);
                                setEditingVenue(true);
                              }, 0);
                            }}
                            disabled={updatingVenueId === venue.id}
                          >
                            Edit
                          </button>
                          <button
                            className="delete-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedVenue(venue);
                              handleDeleteVenue();
                            }}
                            disabled={updatingVenueId === venue.id}
                          >
                            Delete
                          </button>
                        </div>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        
        <div className="field-list-section">
          <div className="section-header">
            <h3>Courts {selectedVenue && `for ${selectedVenue.name}`}</h3>
            {selectedVenue && (
              <button
                className="add-button"
                onClick={() => setIsAddingField(!isAddingField)}
                disabled={!selectedVenue || loadingFields}
              >
                {isAddingField ? 'Cancel' : '+ Add Court'}
              </button>
            )}
          </div>
          
          {loadingFields && fields.length === 0 && selectedVenue && (
            <div className="section-loading">
              <Loading compact message="Loading fields..." />
            </div>
          )}
          
          {isAddingField && selectedVenue && (
            <div className="form-container">
              <form onSubmit={handleAddField}>
                <div className="form-group">
                  <label>Field Name:</label>
                  <input
                    type="text"
                    value={newFieldName}
                    onChange={(e) => setNewFieldName(e.target.value)}
                    placeholder="Enter field name"
                    required
                  />
                </div>
                
                <div className="form-actions">
                  <button type="submit" className="submit-button" disabled={loadingFields}>
                    {loadingFields ? 'Creating...' : 'Create Field'}
                  </button>
                </div>
              </form>
            </div>
          )}
          
          <div className="fields-list">
            {!selectedVenue ? (
              <div className="no-items-message">Please select a venue to manage its fields.</div>
            ) : fields.length === 0 && !loadingFields ? (
              <div className="no-items-message">No fields available for this venue.</div>
            ) : (
              <table className="fields-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {fields.map(field => (
                    <tr key={field.id} className={updatingFieldId === field.id ? 'updating' : ''}>
                      <td>
                        {updatingFieldId === field.id ? (
                          <div className="field-loading">
                            <Loading compact message="" />
                          </div>
                        ) : editingFieldId === field.id ? (
                          <form onSubmit={(e) => handleUpdateField(e, field.id)} className="inline-edit-form">
                            <input
                              type="text"
                              value={editFieldName}
                              onChange={(e) => setEditFieldName(e.target.value)}
                              autoFocus
                            />
                          </form>
                        ) : (
                          field.name
                        )}
                      </td>
                      <td>{new Date(field.createdAt).toLocaleDateString()}</td>
                      <td>
                        {editingFieldId === field.id ? (
                          <div className="field-edit-actions">
                            <button 
                              type="button" 
                              className="save-button"
                              onClick={(e) => handleUpdateField(e, field.id)}
                              disabled={updatingFieldId === field.id}
                            >
                              Save
                            </button>
                            <button 
                              type="button" 
                              className="cancel-button" 
                              onClick={handleCancelFieldEdit}
                              disabled={updatingFieldId === field.id}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <>
                            <button
                              className="edit-button field-action-button"
                              onClick={() => handleEditFieldClick(field)}
                              disabled={updatingFieldId === field.id}
                            >
                              Edit
                            </button>
                            <button
                              className="delete-button"
                              onClick={() => handleDeleteField(field.id)}
                              disabled={updatingFieldId === field.id}
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VenueManagement; 